import { createContext, type ReactNode, useContext } from "react";

// Read-only preview mode for the hire portal. When this context is provided,
// PortalToday/PortalMilestone/PortalContent/PortalSidebarSections render
// directly from the supplied preview payload instead of calling portalRpc.
// All mutation handlers in those components become no-ops.

type PreviewSidebar = {
  hireFirstName: string;
  myJourney: {
    milestones: {
      day: number;
      label: string;
      resourceCount: number;
      todoCount: number;
      itemCount: number;
      completedCount: number;
    }[];
  };
  company: {
    sections: {
      key: string;
      label: string;
      resources: {
        resourceId: string;
        title: string;
        behavior: string;
        sourceUrl: string | null;
      }[];
    }[];
  };
  discipline: {
    blockLabel: string;
    sections: {
      subsectionId: string;
      label: string;
      icon: string;
      resources: {
        resourceId: string;
        title: string;
        behavior: string;
        sourceUrl: string | null;
      }[];
    }[];
  };
};

type PreviewToday = {
  onboardingId: string;
  hireFirstName: string;
  greeting: string;
  eyebrow: string;
  currentDay: number;
  daysRemaining: number;
  state: "active";
  isTerminal: false;
  statusPill: "on_track";
  todayProgress: { done: number; total: number; pending: number };
  items: Array<{
    kind: "todo" | "resource" | "milestone_item" | "tool_setup";
    id: string;
    title: string;
    description: string | null;
    link: string | null;
    targetDate: string | null;
    done: boolean;
    badge: string;
    refs: {
      todoId?: string;
      resourceId?: string;
      resourceBehavior?: string | null;
      resourceRenderType?: string | null;
      milestoneId?: string;
      milestoneDay?: number;
      itemIndex?: number;
      toolSetupId?: string;
    };
  }>;
  banners: { toolSetup: false; csat: false };
  welcomeMessages: Record<string, string>;
};

type PreviewMilestone = {
  onboardingId: string;
  milestoneId: string;
  day: number;
  label: string;
  goal: string;
  readOnly: true;
  expected: { index: number; title: string; description: string | null; checked: boolean }[];
  notExpected: string[];
  progress: { done: number; total: number };
  assignedResources: {
    resourceId: string;
    title: string;
    renderType: string | null;
    behavior: string;
    sourceUrl: string | null;
    actionLabel: string;
    opened: boolean;
    completed: boolean;
  }[];
  dueTodos: {
    todoId: string;
    title: string;
    description: string | null;
    link: string | null;
    done: boolean;
    targetDate: string | null;
  }[];
};

export type PreviewPortalValue = {
  templateId: string;
  availableDays: readonly number[];
  today: PreviewToday;
  milestone: PreviewMilestone;
  sidebar: PreviewSidebar;
  milestoneSelected: number;
  setMilestoneSelected: (day: number) => void;
  /** When set, ResourceRow renders as a button that calls this instead of
   *  navigating to /onboarding/content/:resourceId. Used by the template
   *  preview page to show resources in an inline pane. */
  onResourceNavigate?: (resourceId: string) => void;
  /** Hides the "Dia N de M · X% concluído" eyebrow and the "Boas-vindas, X 👋"
   *  greeting. The unified onboarding dashboard sets this to true because
   *  that information is already rendered in the management header. */
  hideWelcomeHeader?: boolean;
};

const PreviewPortalContext = createContext<PreviewPortalValue | null>(null);

export function PreviewPortalProvider({
  value,
  children,
}: {
  value: PreviewPortalValue;
  children: ReactNode;
}) {
  return <PreviewPortalContext.Provider value={value}>{children}</PreviewPortalContext.Provider>;
}

export function usePreviewPortal(): PreviewPortalValue | null {
  return useContext(PreviewPortalContext);
}
